import publicAxios, { AxiosResponse } from 'axios';
import qs from 'qs';

import { PDFFormDropdownOptionsErrorResponse, PDFFormDropdownOptionsResponse } from 'models/PDFFormDropdownOptions';
import { AddressZipsResponse } from 'models/AddressZipsResponse';
import { ReportsListResponse } from 'models/Report';
import { DownloadReportResponse } from 'models/DownloadReport';
import {
  DbdAccountDetailsResponse,
  DbdAccountInvoiceListPayload,
  DbdAccountInvoiceListResponse,
  DbdAccountsResponse,
  DbdInvoicesResponse,
  DbdStatementDetailsResponse,
  DbdStatementsResponse,
  DbdTotalInvoiceSummaryResponse,
  IPortalCheckListSuccessResponse,
  ISspUsersMetricsResponse,
  GetFormContentErrorResponse,
  GetFormContentResponse,
  LoginDetails,
  RegisterUserDetails,
  RegisterUserDetailsResponse,
  TimeslotDetailsResponse,
  UpdateTimeslotPayload,
  ViewSSPUserDetails,
  ViewSSPUserDetailsResponse,
  IPortalCheckTasksPayload,
  IPortalCheckTasksSuccessResponse,
  IPortalCheckSearchPatientPayload,
  IPortalCheckSearchPatientSuccessResponse,
  SearchProductsPayload,
  ProductsSuccessResponse,
  IPortalCheckStartStopPayload,
  IPortalCheckStartStopSuccessResponse,
  ICreateNewPatientPayload,
  ICreateNewPatientSuccessResponse,
  ICreateTaskPayload,
  ICreateTaskSuccessResponse,
  IEmailFaxDocumentsSuccessResponse,
  IEmailFaxDocumentsPayload,
  IPortalCheckSearchPatientResponseData,
  IEscalationTasksPayload,
  IEscalationTasksSuccessResponse,
  IPaginationPayload,
  ICreateEscalationTasksSuccessResponse,
  IInsurancePayersSuccessResponse,
} from 'models';

import { IManualTaskData, IManualTasksSuccessResponse } from 'models/ManualTask';
import Config from '../Config';
import { CustomerInterfaceResponse } from '../models/CustomerInterfaceResponse';
import { OrderResponseInterface } from '../models/OrderResponseInterface';
import { ChangeUserPasswordDetails, ChangeUserPasswordResponse } from '../models/ChangeUserPasswordDetails';
import { CurrentUserDetailsResponse } from '../models/CurrentUserDetails';
import { FormsAndDocumentsMapResponse } from '../models/FormsAndDocumentsMap';
import { LoginResponse } from '../models/LoginResponse';
import { ResetUserPasswordDetails, ResetUserPasswordResponse } from '../models/ResetUserPasswordDetails';
import {
  SendNewFormsAndDocumentsInterface,
  SendNewFormsAndDocumentsResponseInterface,
} from '../models/SendNewFormsAndDocumentsInterface';
import {
  ViewAllUsersFormsInterface,
  ViewAllUsersFormsInterfaceResponseInterface,
} from '../models/ViewAllUsersFormsInterface';
import { ViewUserDetailsResponse } from '../models/ViewUserDetails';
import axios from './axios';
import { SubmitFormDetails } from '../models/SubmitFormDetails';
import { SubmitFormResponse, SubmitFormErrorResponse } from '../models/SubmitFormResponse';
import { AppointmentDetailsResponse, AppointmentPayload, AppointmentResponse } from '../models/Appointment';
import { StoreLocationResponse } from '../models/StoreLocations';
import { ServiceStatus } from '../models/ServiceStatus';
import { SeriesData } from '../models/SeriesData';

export const fetchData = (): Promise<AxiosResponse> => axios.get('/').then((res) => res.data);

export const postSample = (name: string, result: string): Promise<AxiosResponse> => axios.post('', { name, result });

// Authentication API
export const postLoginDetails = (loginDetails: LoginDetails): Promise<LoginResponse> =>
  axios.post('/api/auth/login', loginDetails).then((res) => res.data);

export const postRegisterUser = (registerUserDetails: RegisterUserDetails): Promise<RegisterUserDetailsResponse> =>
  axios.post('/api/auth/register', registerUserDetails).then((res) => res.data);

export const postChangeUserPassword = (
  changePwDetails: ChangeUserPasswordDetails,
): Promise<ChangeUserPasswordResponse> =>
  axios.post('/api/auth/change-password', changePwDetails).then((res) => res.data);

export const postResetPassword = (resetPwDetails: ResetUserPasswordDetails): Promise<ResetUserPasswordResponse> =>
  axios.post('/api/auth/reset-password', resetPwDetails).then((res) => res.data);

export const getAllUsers = (limit: number, skip = 0): Promise<ViewUserDetailsResponse> =>
  axios.get(`/api/profile/all?${qs.stringify({ limit, skip })}`).then((res) => res.data);

export const getSearchUsers = (query: string): Promise<ViewUserDetailsResponse> =>
  axios.get(`/api/profile/search?${qs.stringify({ query })}`).then((res) => res.data);

export const getCurrentUserDetails = (): Promise<CurrentUserDetailsResponse> =>
  axios.get('/api/profile/me').then((res) => res.data);

export const getAllSspUsers = (limit: number, skip = 0): Promise<ViewSSPUserDetailsResponse> =>
  axios.get(`/api/ssp-profile/all?${qs.stringify({ limit, skip })}`).then((res) => res.data);

export const getCurrentSspUserDetails = (id?: string, customerId?: string): Promise<ViewSSPUserDetails> =>
  axios.get(`/api/ssp-profile?${qs.stringify({ id, customerId })}`).then((res) => res.data.data);

export const deleteUser = (username: string): Promise<CurrentUserDetailsResponse> =>
  axios.delete(`/api/profile?${qs.stringify({ username })}`).then((res) => res.data);

export const getBonafideCustomerDetails = (id: string): Promise<CustomerInterfaceResponse> =>
  axios.get(`/api/ssp-profile/customer?${qs.stringify({ id })}`).then((res) => res.data);

export const getBonafidePatientOrderInformation = (id: string): Promise<OrderResponseInterface> =>
  axios.get(`/api/ssp-profile/order-information?${qs.stringify({ id })}`).then((res) => res.data);

export const getSearchSspUsers = (query: string): Promise<ViewSSPUserDetailsResponse> =>
  axios.get(`/api/ssp-profile/search?${qs.stringify({ query })}`).then((res) => res.data);

export const getPendingOrderForms = (
  hcpcsList?: string[],
  customerIds?: string[],
): Promise<FormsAndDocumentsMapResponse> =>
  axios
    .post('/api/ssp-send-forms-and-documents/pending-order-forms', { hcpcsList, customerIds })
    .then((res) => res.data);

export const getAllForms = (): Promise<any> =>
  axios.get('/api/ssp-send-forms-and-documents/all-forms').then((res) => res.data);

export const postSendNewForms = (
  forms: SendNewFormsAndDocumentsInterface[],
): Promise<SendNewFormsAndDocumentsResponseInterface> =>
  axios.post('/api/ssp-send-forms-and-documents/send-new-forms', { forms }).then((res) => res.data);

export const getAllUsersForms = (limit?: number, skip = 0): Promise<ViewAllUsersFormsInterfaceResponseInterface> =>
  axios
    .get(`/api/ssp-send-forms-and-documents/all-users-forms?${qs.stringify({ limit, skip })}`)
    .then((res) => res.data);

export const getUserFormDetails = (id: string): Promise<ViewAllUsersFormsInterface> =>
  axios.get(`/api/ssp-send-forms-and-documents/form-details?${qs.stringify({ id })}`).then((res) => res.data.data);

export const getFormDropdownOptions = <T = any>(
  formComponent: string,
  version: string,
): Promise<PDFFormDropdownOptionsResponse<T> | PDFFormDropdownOptionsErrorResponse> =>
  axios
    .get(`/api/ssp-send-forms-and-documents/form-dropdown-options/${formComponent}/${version}`)
    .then((res) => res.data);

export const getSearchUsersForms = (query: string): Promise<ViewAllUsersFormsInterfaceResponseInterface> =>
  axios.get(`/api/ssp-send-forms-and-documents/search?${qs.stringify({ query })}`).then((res) => res.data);

export const deleteUserForm = (id: string): Promise<ViewAllUsersFormsInterfaceResponseInterface> =>
  axios.delete(`/api/ssp-send-forms-and-documents/delete-user-form?${qs.stringify({ id })}`).then((res) => res.data);

export const getS3ContentBuffer = (objectKey: string): Promise<Blob> =>
  axios
    .get(`/s3/${objectKey}`, {
      baseURL: Config.cdnProxyBaseUrl,
      responseType: 'blob',
    })
    .then((res) => res.data);

export const getFormContent = (
  formComponent: string,
  version: string,
): Promise<GetFormContentResponse | GetFormContentErrorResponse> =>
  axios.get(`/api/forms/${formComponent}/${version}`, { baseURL: Config.contentUrl }).then((res) => res.data);

export const postSubmitForm = (
  id: string,
  details: SubmitFormDetails,
): Promise<SubmitFormResponse | SubmitFormErrorResponse> =>
  axios.post(`/api/ssp-send-forms-and-documents/submit/${id}`, details, { timeout: 300000 }).then((res) => res.data);

export const postSubmitAppointment = (newAppointment: AppointmentPayload): Promise<AppointmentResponse> =>
  axios.post(`/api/ssp-appointments/submit`, newAppointment, { timeout: 300000 }).then((res) => res.data);

export const getAppointments = (month: number, year: number): Promise<AppointmentResponse> =>
  axios.get(`/api/ssp-appointments/all-appointments?${qs.stringify({ month, year })}`).then((res) => res.data);

export const getAppointmentDetails = (id?: string, customerId?: string): Promise<AppointmentDetailsResponse> =>
  axios.get(`/api/ssp-appointments/?${qs.stringify({ id, customerId })}`).then((res) => res.data);

export const deleteAppointment = (
  id: string,
  timeslot: string,
  date: string,
  storeId: string,
): Promise<{ status: 'ok' | 'error' }> =>
  axios
    .delete(`/api/ssp-appointments/delete-appointment?${qs.stringify({ id, timeslot, date, storeId })}`)
    .then((res) => res.data);

export const getAllStoreLocations = (): Promise<StoreLocationResponse> =>
  axios.get(`/api/store-locations/all`).then((res) => res.data);

export const getTimeslotDetails = (storeId: string, date: string): Promise<TimeslotDetailsResponse> =>
  axios.post('/api/ssp-appointments/timeslot-details', { storeId, date }).then((res) => res.data);

export const updateTimeslot = (payload: UpdateTimeslotPayload): Promise<TimeslotDetailsResponse> =>
  axios.post('/api/ssp-appointments/update-timeslot', payload).then((res) => res.data);

export const getAllServiceStatus = (): Promise<ServiceStatus> =>
  publicAxios
    .all([
      axios.get(Config.baseUrl),
      axios.get(Config.sspBaseUrl),
      axios.get(Config.cdnProxyBaseUrl),
      axios.get(Config.contentUrl),
      axios.get(Config.pdfBaseUrl),
    ])
    .then(
      publicAxios.spread((iportal, ssp, cdn, content, pdf) => {
        return {
          iportal: iportal.data,
          ssp: ssp.data,
          cdn: cdn.data,
          content: content.data,
          pdf: pdf.data,
        };
      }),
    );

export const getNewUsersAnalytics = (): Promise<SeriesData> =>
  axios.get(`/api/analytics/new-ssp-users`).then((res) => res.data.data);

export const getAddressZips = (): Promise<AddressZipsResponse> =>
  axios.get(`/api/analytics/address-zips`).then((res) => res.data);

export const getAllReports = (): Promise<ReportsListResponse> => axios.get(`api/reports/all`).then((res) => res.data);

export const downloadReport = (reportId: string): Promise<DownloadReportResponse> =>
  axios.get(`api/reports/download/${reportId}`).then((res) => res.data);

export const getSspUsersMetrics = (): Promise<ISspUsersMetricsResponse> =>
  axios.get(`/api/analytics/ssp-users-metrics`).then((res) => res.data);

export const getDbdInvoices = (limit: number, skip: number): Promise<DbdInvoicesResponse> =>
  axios.get(`/api/dbd/invoices/all?${qs.stringify({ limit, skip })}`).then((res) => res.data);

export const getDbdStatementUploads = (limit: number, skip: number): Promise<DbdStatementsResponse> =>
  axios.get(`/api/dbd/csv?${qs.stringify({ limit, skip })}`).then((res) => res.data);

export const getDbdStatementUploadsDetails = (id: string): Promise<DbdStatementDetailsResponse> =>
  axios.get(`/api/dbd/csv/${id}`).then((res) => res.data);

export const getSearchDbdStatementUploads = (query: string): Promise<DbdStatementsResponse> =>
  axios.get(`/api/dbd/csv/search?${qs.stringify({ query })}`).then((res) => res.data);

export const uploadDbdStatement = (data: FormData): Promise<any> =>
  axios
    .post('/api/dbd/upload-csv', data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    .then((res) => res.data);

export const getDbdAccounts = (limit: number, skip: number): Promise<DbdAccountsResponse> =>
  axios.get(`/api/dbd/accounts/all?${qs.stringify({ limit, skip })}`).then((res) => res.data);

export const getDbdAccountDetails = (id: string): Promise<DbdAccountDetailsResponse> =>
  axios.get(`/api/dbd/accounts/get-one/${id}`).then((res) => res.data);

export const getSearchDbdAccounts = (query: string): Promise<DbdAccountsResponse> =>
  axios.get(`/api/dbd/accounts/search?${qs.stringify({ query })}`).then((res) => res.data);

export const getDbdAccountInvoiceList = (body: DbdAccountInvoiceListPayload): Promise<DbdAccountInvoiceListResponse> =>
  axios.post('/api/dbd/accounts/invoices-list', body).then((res) => res.data);

export const getDbdTotalInvoiceSummary = (): Promise<DbdTotalInvoiceSummaryResponse> =>
  axios.get('/api/dbd/invoices/summary/all').then((res) => res.data);

export const getDbdInvoiceSummary = (id: string): Promise<any> =>
  axios.get(`/api/dbd/invoices/summary?id=${id}`).then((res) => res.data);

export const getIcd10CmList = (searchQuery: string): Promise<[any[], any[], any[], any[]]> =>
  axios
    .get(
      `https://clinicaltables.nlm.nih.gov/api/icd10cm/v3/search?sf=code,name&df=code,name&maxList=500&terms=${searchQuery}`,
    )
    .then((res) => res.data);

export const getNPIDirectoryList = (searchQuery: string): Promise<[any[], any[], any[], any[]]> =>
  axios
    .get(
      `https://clinicaltables.nlm.nih.gov/api/npi_idv/v3/search?sf=NPI,provider_type,gender,name.full,addr_practice.full,licenses,licenses.taxonomy,licenses.taxonomy.code,licenses.taxonomy.grouping,licenses.taxonomy.classification,licenses.taxonomy.specialization,licenses.medicare,licenses.medicare.spc_code,licenses.medicare.type,name,name.last,name.first,name.middle,name.credential,name.prefix,name.suffix,addr_practice,addr_practice.line1,addr_practice.line2,addr_practice.city,addr_practice.state,addr_practice.zip,addr_practice.phone,addr_practice.fax,addr_practice.country,addr_mailing,addr_mailing.full,addr_mailing.line1,addr_mailing.line2,addr_mailing.city,addr_mailing.state,addr_mailing.zip,addr_mailing.phone,addr_mailing.fax,addr_mailing.country,name_other,name_other.full,name_other.last,name_other.first,name_other.middle,name_other.credential,name_other.prefix,name_other.suffix,other_ids,other_ids.id,other_ids.type,other_ids.issuer,other_ids.state,misc,misc.auth_official,misc.auth_official.last,misc.auth_official.first,misc.auth_official.middle,misc.auth_official.credential,misc.auth_official.title,misc.auth_official.prefix,misc.auth_official.suffix,misc.auth_official.phone,misc.replacement_NPI,misc.EIN,misc.enumeration_date,misc.last_update_date,misc.is_sole_proprietor,misc.is_org_subpart,misc.parent_LBN,misc.parent_TIN&df=NPI,provider_type,gender,name.full,addr_practice.full,licenses,licenses.taxonomy,licenses.taxonomy.code,licenses.taxonomy.grouping,licenses.taxonomy.classification,licenses.taxonomy.specialization,licenses.medicare,licenses.medicare.spc_code,licenses.medicare.type,name,name.last,name.first,name.middle,name.credential,name.prefix,name.suffix,addr_practice,addr_practice.line1,addr_practice.line2,addr_practice.city,addr_practice.state,addr_practice.zip,addr_practice.phone,addr_practice.fax,addr_practice.country,addr_mailing,addr_mailing.full,other_ids,other_ids.id,other_ids.type,other_ids.issuer,other_ids.state&terms=${searchQuery}&maxList=500`,
    )
    .then((res) => res.data);

export const getAllPortalCheckList = (): Promise<IPortalCheckListSuccessResponse> =>
  axios.get('/api/task-manager/portal-check/all').then((res) => res.data);

export const getAllPortalCheckTasks = (payload: IPortalCheckTasksPayload): Promise<IPortalCheckTasksSuccessResponse> =>
  axios.get(`/api/task-manager/all-tasks?${qs.stringify({ ...payload })}`).then((res) => res.data);

export const getPortalCheckSearchPatient = (
  payload: IPortalCheckSearchPatientPayload,
): Promise<IPortalCheckSearchPatientSuccessResponse> =>
  axios.get(`/api/task-manager/portal-check/search-patient?${qs.stringify({ ...payload })}`).then((res) => res.data);

export const getPortalCheckPatient = (id: string): Promise<IPortalCheckSearchPatientResponseData> =>
  axios.get(`/api/task-manager/portal-check/patient?${qs.stringify({ id })}`).then((res) => res.data.data);

export const putStartPortalCheckTask = (
  payload: IPortalCheckStartStopPayload,
): Promise<IPortalCheckStartStopSuccessResponse> =>
  axios.put('/api/task-manager/portal-check/start', payload).then((res) => res.data);

export const putStopPortalCheckTask = (
  payload: IPortalCheckStartStopPayload,
): Promise<IPortalCheckStartStopSuccessResponse> =>
  axios.put('/api/task-manager/portal-check/stop', payload).then((res) => res.data);

export const putCompletePortalCheckTask = (
  payload: IPortalCheckStartStopPayload,
): Promise<IPortalCheckStartStopSuccessResponse> =>
  axios.put('/api/task-manager/portal-check/complete', payload).then((res) => res.data);

export const postCreateNewPatient = (payload: ICreateNewPatientPayload): Promise<ICreateNewPatientSuccessResponse> =>
  axios.post('/api/task-manager/portal-check/create-patient', payload).then((res) => res.data);

export const postCreateTask = (payload: ICreateTaskPayload): Promise<ICreateTaskSuccessResponse> =>
  axios.post('/api/task-manager/create-task', payload).then((res) => res.data);

export const putUpdateTask = (
  payload: Omit<ICreateTaskPayload, '_id'> & { id: string },
): Promise<ICreateTaskSuccessResponse> => axios.put('/api/task-manager/update-task', payload).then((res) => res.data);

export const getEmailFaxDocuments = (type: IEmailFaxDocumentsPayload): Promise<IEmailFaxDocumentsSuccessResponse> => {
  return axios.get(`/api/task-manager/email-fax-documents/all?${qs.stringify({ type })}`).then((res) => res.data);
};

export const postCreateEscalation = (
  payload: IEscalationTasksPayload,
): Promise<ICreateEscalationTasksSuccessResponse> =>
  axios.post('/api/task-manager/escalations/create', payload).then((res) => res.data);

export const getEscalationTasks = (payload: IPaginationPayload): Promise<ICreateEscalationTasksSuccessResponse> =>
  axios.get(`/api/task-manager/escalations/all?${qs.stringify({ ...payload })}`).then((res) => res.data);

export const getMyEscalationTasks = (payload: IPaginationPayload): Promise<IEscalationTasksSuccessResponse> =>
  axios.get(`/api/task-manager/escalations/me?${qs.stringify({ ...payload })}`).then((res) => res.data);

export const getEscalationDetails = (id: string): Promise<ICreateEscalationTasksSuccessResponse> =>
  axios.get(`/api/task-manager/escalations/${id}`).then((res) => res.data);

export const updateEscalationById = (
  id: string,
  payload: IEscalationTasksPayload,
): Promise<ICreateEscalationTasksSuccessResponse> =>
  axios.put(`/api/task-manager/escalations/${id}`, payload).then((res) => res.data);

export const deleteEscalationById = (id: string): Promise<IEscalationTasksSuccessResponse> => {
  return axios.delete(`/api/task-manager/escalations/${id}`).then((res) => res.data);
};

export const postCreateEscalationThread = (id: string, payload: { message: string }) =>
  axios.post(`/api/task-manager/escalations/thread/${id}`, payload).then((res) => res.data);

export const getAllInsurancePayers = (): Promise<IInsurancePayersSuccessResponse> =>
  axios.get('/api/task-manager/payers/all').then((res) => res.data);

export const getPayerByInsuranceName = (insuranceName: string): Promise<IInsurancePayersSuccessResponse> =>
  axios.get(`/api/task-manager/payers/insurance/${insuranceName}`).then((res) => res.data);

// @TODO: Strictly type this
export const submitTask = (payload: any): Promise<any> =>
  axios
    .post('/api/task-manager/submit', payload, {
      // form data that can pass images
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    .then((res) => res.data);

export const getSubmittedTasks = (status: string, payload: IPaginationPayload): Promise<any> =>
  axios.get(`/api/task-manager/submit/${status}?${qs.stringify({ ...payload })}`).then((res) => res.data);

export const getManualTasks = (): Promise<IManualTasksSuccessResponse> =>
  axios.get('/api/task-manager/manual-tasks/all').then((res) => res.data);

export const postCreateManualTask = (payload: IManualTaskData): Promise<IManualTasksSuccessResponse> =>
  axios.post('/api/task-manager/manual-tasks/create', payload).then((res) => res.data);

export const putUpdateManualTask = (id: string, payload: IManualTaskData): Promise<IManualTasksSuccessResponse> =>
  axios.put(`/api/task-manager/manual-tasks/${id}`, payload).then((res) => res.data);

export const getManualTaskDetails = (id: string): Promise<IManualTasksSuccessResponse> =>
  axios.get(`/api/task-manager/manual-tasks/${id}`).then((res) => res.data);

export const getSearchProducts = (payload: SearchProductsPayload): Promise<ProductsSuccessResponse> =>
  axios.get(`/api/task-manager/products?${qs.stringify({ ...payload })}`).then((res) => res.data);
